import * as Sentry from '@sentry/react';
import createDebug from 'debug';
import { invert, snakeCase } from 'lodash';

import { DebugKey } from '@swf/core/enums';
import { StatusCode } from '@swf/core/enums/statusCode';

import type { ResponseHeaders } from '../types/Request';
import { canUseDOM } from '../utils';

import { TXTWCL_Sorry___an_error_has_occurred } from './RequestError.messages.json';

const debug = createDebug(DebugKey.Request);
const statusKeys = invert(StatusCode);

export interface ErrorBody {
  code: string;
  message?: string;
  text: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isErrorBody = (content: any): content is ErrorBody => !!content.code;

export class RequestError<B = ErrorBody> extends Error {
  headers?: ResponseHeaders;
  body: B & { message: string };
  private response?: Response;
  private service: string;

  constructor({
    service,
    body,
    response,
    method,
    headers
  }: {
    service: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body: ErrorBody | any;
    response?: Response;
    method: string;
    headers?: ResponseHeaders;
  }) {
    const statusCode = response?.status;
    const name = statusCode && statusCode >= 400 ? statusCode : method;

    if (isErrorBody(body)) {
      super(`${name} ${service} ${body.code}`);
    } else if (headers?.statusCode) {
      const { statusCode: errorCode } = headers;
      let displayCode = statusKeys[errorCode];

      if (!displayCode) {
        displayCode = snakeCase(errorCode as string).toUpperCase();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (body as any).message = TXTWCL_Sorry___an_error_has_occurred.defaultMessage;
      }

      super(`${name} ${service} ${displayCode}`);
    } else {
      super(`${name} ${service}`);
    }

    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'request-error',
      message: this.message,
      data: {
        method,
        service,
        statusCode: response?.status
      }
    });

    // https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
    Object.setPrototypeOf(this, RequestError.prototype);

    this.name = 'RequestError';
    this.body = body;
    this.response = response;
    this.service = service;

    if (headers) this.headers = headers;
    else if (statusCode) this.headers = { statusCode };
  }

  log() {
    debug('failed %s %s', this.service, this.message.replace(` ${this.service}`, ''));
    if (process.env.NODE_ENV !== 'test' && (!this.response || this.response.ok) && canUseDOM) {
      // eslint-disable-next-line no-console
      console.error(this.message);
    }
  }
}

export const isRequestErrorWithCode = (err: unknown): err is RequestError<{ code: string }> =>
  err instanceof RequestError && 'code' in err.body;
